/**
 * Edge Selection Service
 *
 * Manages edge selection state and deletion via keyboard.
 * When an edge is clicked, it becomes selected (visual feedback).
 * Delete/Backspace removes the selected edge.
 *
 * @see sl-rud for specification
 */

import type { GfxController } from '@blocksuite/std/gfx';

import type { EdgeRenderingService } from '../rendering/edge-rendering-service';
import type { StreamlainEdge } from '../types';

/**
 * Callback to delete an edge from the model
 */
export type EdgeDeleteHandler = (edge: StreamlainEdge) => Promise<boolean>;

/**
 * Callback to show confirmation dialog for edge deletion
 * Receives edge type so caller can handle i18n
 */
export type ConfirmDeleteHandler = (
  edgeType: 'automation' | 'relation'
) => Promise<boolean>;

/**
 * Configuration for the selection service
 */
export interface EdgeSelectionConfig {
  /** Enable edge selection */
  enabled: boolean;
  /** Show confirmation dialog before delete */
  confirmDelete: boolean;
}

const DEFAULT_CONFIG: EdgeSelectionConfig = {
  enabled: true,
  confirmDelete: true,
};

/**
 * Edge Selection Service
 *
 * Handles edge selection and deletion via keyboard.
 */
export class EdgeSelectionService {
  private config: EdgeSelectionConfig;
  private mounted = false;
  private container: HTMLElement | null = null;
  private cleanupFns: Array<() => void> = [];

  // Selection state
  private selectedEdgeId: string | null = null;

  constructor(
    private readonly renderingService: EdgeRenderingService,
    private readonly gfx: GfxController,
    private readonly onDelete: EdgeDeleteHandler,
    private readonly onConfirmDelete: ConfirmDeleteHandler,
    config: Partial<EdgeSelectionConfig> = {}
  ) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // ===========================================================================
  // Lifecycle
  // ===========================================================================

  /**
   * Mounts the selection service to observe a container
   */
  mount(container: HTMLElement): void {
    if (this.mounted) {
      this.unmount();
    }

    this.container = container;
    this.mounted = true;

    this.setupClickHandler();
    this.setupKeyboardHandler();
  }

  /**
   * Unmounts the service and cleans up
   */
  unmount(): void {
    for (const cleanup of this.cleanupFns) {
      cleanup();
    }
    this.cleanupFns = [];

    this.clearSelection();
    this.container = null;
    this.mounted = false;
  }

  /**
   * Checks if the service is mounted
   */
  isMounted(): boolean {
    return this.mounted;
  }

  // ===========================================================================
  // Click Handling
  // ===========================================================================

  /**
   * Sets up click event handling on the container
   */
  private setupClickHandler(): void {
    if (!this.container) return;

    const handleClick = (e: MouseEvent) => {
      if (!this.config.enabled) return;

      // Check if we clicked on an edge
      const edge = this.renderingService.getEdgeAtPoint({
        x: e.clientX,
        y: e.clientY,
      });

      if (edge) {
        // Select this edge
        this.selectEdge(edge.id);
        e.preventDefault();
        e.stopPropagation();
      } else {
        // Clicked elsewhere - clear selection
        this.clearSelection();
      }
    };

    // Use capture phase to intercept before navigation handler
    this.container.addEventListener('click', handleClick, { capture: true });

    this.cleanupFns.push(() => {
      this.container?.removeEventListener('click', handleClick, {
        capture: true,
      });
    });
  }

  // ===========================================================================
  // Keyboard Handling
  // ===========================================================================

  /**
   * Sets up keyboard event handling for delete
   */
  private setupKeyboardHandler(): void {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!this.config.enabled) return;
      if (!this.selectedEdgeId) return;

      // Delete or Backspace
      if (e.key === 'Delete' || e.key === 'Backspace') {
        // Don't delete if user is typing in an input
        const target = e.target as HTMLElement;
        if (
          target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable
        ) {
          return;
        }

        e.preventDefault();
        e.stopPropagation();
        this.deleteSelectedEdge().catch(console.error);
      }

      // Escape clears selection
      if (e.key === 'Escape') {
        this.clearSelection();
      }
    };

    // Listen on document for keyboard events
    document.addEventListener('keydown', handleKeyDown);

    this.cleanupFns.push(() => {
      document.removeEventListener('keydown', handleKeyDown);
    });
  }

  // ===========================================================================
  // Selection Logic
  // ===========================================================================

  /**
   * Selects an edge by ID
   */
  selectEdge(edgeId: string): void {
    // Clear previous selection
    if (this.selectedEdgeId && this.selectedEdgeId !== edgeId) {
      this.renderingService.setEdgeSelected(this.selectedEdgeId, false);
    }

    // Set new selection
    this.selectedEdgeId = edgeId;
    this.renderingService.setEdgeSelected(edgeId, true);

    // Clear BlockSuite element selection to avoid confusion
    this.gfx.selection?.clear();
  }

  /**
   * Clears the current edge selection
   */
  clearSelection(): void {
    if (this.selectedEdgeId) {
      this.renderingService.setEdgeSelected(this.selectedEdgeId, false);
      this.selectedEdgeId = null;
    }
  }

  /**
   * Gets the currently selected edge ID
   */
  getSelectedEdgeId(): string | null {
    return this.selectedEdgeId;
  }

  // ===========================================================================
  // Deletion Logic
  // ===========================================================================

  /**
   * Deletes the currently selected edge
   */
  private async deleteSelectedEdge(): Promise<void> {
    if (!this.selectedEdgeId) return;

    const edge = this.renderingService.getEdge(this.selectedEdgeId);
    if (!edge) {
      this.clearSelection();
      return;
    }

    // Show confirmation dialog if enabled
    if (this.config.confirmDelete) {
      const confirmed = await this.onConfirmDelete(edge.type);

      if (!confirmed) {
        return;
      }
    }

    // Delete the edge
    const deleted = await this.onDelete(edge);

    if (deleted) {
      this.clearSelection();
    }
  }

  // ===========================================================================
  // Public API
  // ===========================================================================

  /**
   * Updates configuration at runtime
   */
  setConfig(config: Partial<EdgeSelectionConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Gets current configuration
   */
  getConfig(): EdgeSelectionConfig {
    return { ...this.config };
  }

  /**
   * Enables or disables selection
   */
  setEnabled(enabled: boolean): void {
    this.config.enabled = enabled;
    if (!enabled) {
      this.clearSelection();
    }
  }

  /**
   * Checks if selection is enabled
   */
  isEnabled(): boolean {
    return this.config.enabled;
  }
}
